import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useSnackbar } from 'notistack';

import {
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Page from '../../components/Page';
import { ConfigService } from '../../Services/config.service';
import { FormProvider, RHFTextField } from '../../components/hook-form';
import { useLoadingContext } from '../../context/loadingContext';

const RecruitSchema = Yup.object().shape({
  name: Yup.string().required('Name không đươc để trống'),
  value: Yup.string().required('Value không được để trống'),
});

const Config = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [configs, setConfigs] = useState([]);
  const [key, setKey] = useState(null);
  const [id, setId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [province, setProvince] = useState([]);
  const [editProvince, setEditProvince] = useState(false);
  const [newProvince, setNewProvince] = useState(false);
  const [name, setName] = useState('');
  const { handleStartLoading, handleStopLoading } = useLoadingContext();

  useEffect(() => {
    getConfig().finally(handleStopLoading);
    getProvince();
  }, []);

  const handleEdit = async (key, id, name, value) => {
    setKey(key);
    setId(id);
    setEditMode(true);
    setValue('name', name);
    setValue('value', value);
  };

  const handleEditProvince = async (id, name) => {
    setId(id);
    setEditProvince(true);
    setName(name);
  };

  const handleNewProvince = async () => {
    setNewProvince(true);
  };

  const getConfig = async () => {
    handleStartLoading();
    const res = await ConfigService.GetConfig();
    setConfigs(res);
  };

  const getProvince = async () => {
    const res = await ConfigService.GetProvince();
    setProvince(res);
  };

  const showConfig = (key, id) => {
    handleStartLoading();
    ConfigService.ShowConfig(key, id)
      .then(() => enqueueSnackbar('Thành công', { variant: 'success' }))
      .then(() => getConfig())
      .catch((err) => enqueueSnackbar(err.response.data), { variant: ' error' })
      .finally(handleStopLoading);
  };

  const hiddenConfig = (key, id) => {
    handleStartLoading();
    ConfigService.HiddenConfig(key, id)
      .then(() => enqueueSnackbar('Thành công', { variant: 'success' }))
      .then(() => getConfig())
      .catch((err) => enqueueSnackbar(err.response.data), { variant: ' error' })
      .finally(handleStopLoading);
  };

  /** React hook form --------------------------------- */
  const defaultValues = {
    name: '',
    value: '',
  };

  const methods = useForm({
    resolver: yupResolver(RecruitSchema),
    defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleOnSubmit = (data) => {
    handleStartLoading();
    ConfigService.EditConfig({ ...data }, key, id)
      .then((res) => {
        enqueueSnackbar('Cập nhật config thành công', { variant: 'success' });
      })
      .then(() => {
        getConfig();
        setEditMode(false);
      })
      .catch((err) => {
        enqueueSnackbar(err.response.data, { variant: 'error' });
      })
      .finally(handleStopLoading);
  };

  const handleOnSubmitProvince = () => {
    if (name === null || name.length === 0) return enqueueSnackbar('Tên không được để trống', { variant: 'error' });
    handleStartLoading();
    ConfigService.EditProvince({ name }, id)
      .then((res) => {
        enqueueSnackbar('Cập nhật thành công', { variant: 'success' });
      })
      .then(() => {
        setName('');
        getProvince();
        setEditProvince(false);
      })
      .catch((err) => {
        enqueueSnackbar(err.response.data, { variant: 'error' });
      })
      .finally(handleStopLoading);
  };

  const handleOnSubmitNewProvince = () => {
    if (name === null || name.length === 0) return enqueueSnackbar('Tên không được để trống', { variant: 'error' });
    ConfigService.CreateProvince({ name })
      .then((res) => {
        enqueueSnackbar('Tao moi thành công', { variant: 'success' });
        setNewProvince(false);
      })
      .then(() => {
        setName('');
        setEditProvince(false);
        getProvince();
      })
      .catch((err) => {
        enqueueSnackbar(err.response.data, { variant: 'error' });
      });
  };

  return (
    <Page title="Config">
      <Container maxWidth="xl">
        {editMode && (
          <Dialog open maxWidth="xs" fullWidth>
            <DialogTitle>Cập nhật</DialogTitle>
            <FormProvider methods={methods} onSubmit={handleSubmit(handleOnSubmit)}>
              <DialogContent>
                <RHFTextField name="name" label={'Name'} fullWidth variant="standard" disabled />
                <RHFTextField name="value" sx={{ mt: 1 }} label={'Value'} fullWidth variant="standard" />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditMode(false)}>Hủy</Button>
                <Button type="submit">Cập nhật</Button>
              </DialogActions>
            </FormProvider>
          </Dialog>
        )}
        {editProvince && (
          <Dialog open maxWidth="xs" fullWidth>
            <DialogTitle>Cập nhật</DialogTitle>
            <DialogContent>
              <TextField
                name="name"
                label={'Name'}
                fullWidth
                variant="standard"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditProvince(false)}>Hủy</Button>
              <Button onClick={handleOnSubmitProvince}>Cập nhật</Button>
            </DialogActions>
          </Dialog>
        )}
        {newProvince && (
          <Dialog open maxWidth="xs" fullWidth>
            <DialogTitle>Tạo Tỉnh</DialogTitle>
            <DialogContent>
              <TextField
                name="name"
                label={'Name'}
                fullWidth
                variant="standard"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewProvince(false)}>Hủy</Button>
              <Button onClick={handleOnSubmitNewProvince}>OK</Button>
            </DialogActions>
          </Dialog>
        )}
        <Typography sx={{ mb: 5 }} variant="h4" gutterBottom>
          Config
        </Typography>
        <Stack spacing={3}>
          {configs.map((config) => (
            <Card sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="h4" gutterBottom>
                    {config?.name}
                  </Typography>
                </Stack>
              </Stack>
              <Stack spacing={2}>
                {config.configData.map((item) => (
                  <div>
                    <Stack sx={{ pl: 4 }} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack spacing={1} direction="row">
                        <Typography variant="h6" gutterBottom>
                          {item.name}
                        </Typography>
                        {item.isShow ? (
                          <Chip color="primary" size="small" label="Hoạt động" />
                        ) : (
                          <Chip color="error" size="small" label="Không hoạt động" />
                        )}
                      </Stack>

                      <Typography variant="h6" gutterBottom>
                        {item.value}
                      </Typography>
                    </Stack>
                    <Stack justifyContent="end" direction="row" spacing={1}>
                      {item.isShow ? (
                        <Button
                          onClick={() => hiddenConfig(config.key, item._id)}
                          color="error"
                          size="small"
                          variant="outlined"
                        >
                          Ẩn
                        </Button>
                      ) : (
                        <Button
                          color="success"
                          size="small"
                          variant="outlined"
                          onClick={() => showConfig(config.key, item._id)}
                        >
                          Hiện
                        </Button>
                      )}
                      {config.key !== 'config' && (
                        <Button
                          onClick={() => handleEdit(config.key, item._id, item.name, item.value)}
                          color="info"
                          size="small"
                          variant="outlined"
                        >
                          Cập nhật
                        </Button>
                      )}
                    </Stack>
                  </div>
                ))}
              </Stack>
            </Card>
          ))}
          <Card sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h4" gutterBottom>
                  Tỉnh
                </Typography>
              </Stack>
              <Button onClick={() => handleNewProvince()} color="info" size="small" variant="outlined">
                Thêm
              </Button>
            </Stack>
            <Stack spacing={2}>
              {province.map((item) => (
                <div>
                  <Stack sx={{ pl: 4 }} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack spacing={1} direction="row">
                      <Typography variant="h6" gutterBottom>
                        {item.name}
                      </Typography>
                    </Stack>

                    <Button
                      onClick={() => {
                        handleEditProvince(item._id, item.name);
                      }}
                      color="info"
                      size="small"
                      variant="outlined"
                    >
                      Cập nhật
                    </Button>
                  </Stack>
                </div>
              ))}
            </Stack>
          </Card>
        </Stack>
      </Container>
    </Page>
  );
};

export default Config;
